import { alpha, hexToRgb } from '@mui/system';
import Button from '@mui/material/Button';

import { getTypographyStyles, styled } from 'helpers';

import { ButtonProps } from '.';

export const StyledButton = styled(Button)<ButtonProps>`
  ${({ theme }) => getTypographyStyles('button', theme)}

  box-shadow: none;
  box-sizing: border-box;
  line-height: 1;

  &:hover,
  &:active {
    box-shadow: none;
  }

  &.MuiDisabled {
    color: ${({ theme }) => theme.palette.blueGrey['A200']};
    cursor: not-allowed;
  }

  &.MuiButton-sizeSmall {
    font-size: ${({ theme }) => theme.typography.caption.fontSize};
    padding: 0.5rem 1rem;
  }

  &.MuiButton-sizeMedium {
    padding: 0.75rem 2rem;
    &.MuiButton-text {
      padding: 0.75rem 0.5rem;
    }
  }

  &.MuiButton-sizeLarge {
    padding: 1rem 2.25rem;
  }

  &.MuiButton-containedPrimary {
    background-color: ${({ theme }) => theme.palette.primary.main};
    color: ${({ theme }) => theme.palette.primary.contrastText};
    &:hover,
    &:active {
      background-color: ${({ theme }) => theme.palette.primary.dark};
    }
    &.Mui-disabled {
      background-color: ${({ theme }) => alpha(hexToRgb(theme.palette.primary.main), 0.24)};
    }
  }

  &.MuiButton-containedSecondary {
    background-color: ${({ theme }) => theme.palette.secondary.main};
    color: ${({ theme }) => theme.palette.secondary.contrastText};
    &:hover,
    &:active {
      background-color: ${({ theme }) => theme.palette.secondary.dark};
    }
    &.Mui-disabled {
      color: ${({ theme }) => theme.palette.blueGrey['A200']};
    }
    &.Mui-focusVisible {
      background-color: ${({ theme }) => theme.palette.secondary.dark};
      box-shadow: none;
      transition: none;
    }
  }

  &.MuiButton-containedDanger {
    &:hover,
    &:active {
      background-color: ${({ theme }) => theme.palette.danger.dark};
    }
    &.Mui-disabled {
      background-color: ${({ theme }) => alpha(hexToRgb(theme.palette.danger.main), 0.24)};
      color: ${({ theme }) => theme.palette.common.white};
    }
  }

  &.MuiButton-outlined {
    border-color: ${({ theme }) => theme.palette.primary.main};
    border-width: 2px;
    padding: 0.625rem 1.875rem;

    &.MuiButton-sizeSmall {
      padding: 0.375rem 1.625rem;
    }

    &.MuiButton-sizeLarge {
      padding: 0.875rem 2.125rem;
    }

    &.MuiButton-outlinedPrimary {
      &:hover,
      &:active {
        background-color: ${({ theme }) => theme.palette.blue['A50']};
      }
      &.Mui-disabled {
        border-color: ${({ theme }) => theme.palette.secondary.main};
        color: ${({ theme }) => theme.palette.blueGrey['A200']};
      }
    }

    &.MuiButton-outlinedSecondary {
      border-color: ${({ theme }) => theme.palette.secondary.dark};
      color: ${({ theme }) => theme.palette.secondary.contrastText};
      &:hover,
      &:active {
        background-color: ${({ theme }) => theme.palette.grey[50]};
      }
      &.Mui-disabled {
        border-color: ${({ theme }) => theme.palette.secondary.main};
        color: ${({ theme }) => theme.palette.blueGrey['A200']};
      }
    }

    &.MuiButton-outlinedDanger {
      border-color: ${({ theme }) => theme.palette.danger.main};
      &:hover,
      &:active {
        background-color: ${({ theme }) => theme.palette.danger.light};
      }
      &.Mui-disabled {
        border-color: ${({ theme }) => theme.palette.secondary.main};
        color: ${({ theme }) => theme.palette.blueGrey['A200']};
      }
    }
  }

  &.MuiButton-text {
    &.MuiButton-textPrimary {
      color: ${({ theme }) => theme.palette.primary.main};
      &:hover,
      &:active {
        background-color: ${({ theme }) => theme.palette.blue[50]};
      }
      &.Mui-disabled {
        color: ${({ theme }) => alpha(hexToRgb(theme.palette.primary.main), 0.4)};
        cursor: not-allowed;
      }
    }

    &.MuiButton-textSecondary {
      color: ${({ theme }) => theme.palette.secondary.contrastText};
      &:hover,
      &:active {
        background-color: ${({ theme }) => theme.palette.grey[50]};
      }
      &.Mui-disabled {
        color: ${({ theme }) => theme.palette.blueGrey['A200']};
      }
    }

    &.MuiButton-textDanger {
      color: ${({ theme }) => theme.palette.danger.dark};
      &:hover,
      &:active {
        background-color: ${({ theme }) => theme.palette.danger.light};
      }
      &.Mui-disabled {
        color: ${({ theme }) => alpha(hexToRgb(theme.palette.primary.main), 0.4)};
      }
    }
  }
`;
StyledButton.displayName = 'Button';
